import type { ReactNode } from "react";
import { Box, Skeleton, Text, Title } from "@mantine/core";
import styles from "./styles/PageHero.module.css";

interface PageHeroProps {
    /** Small line above the title, e.g. "Kategori · 12 poddar" */
    eyebrow: ReactNode;
    /** The page's `h1` */
    title: ReactNode;
    lead?: ReactNode;
    /** Maximum width of the lead, in characters. Defaults to 46. */
    leadWidth?: number;
    /** Buttons or controls under the lead */
    children?: ReactNode;
    /** Shown beside the text; without it the hero is text only */
    aside?: ReactNode;
    /** A full-width row under the hero, set off by a rule */
    footer?: ReactNode;
    /** Show placeholders for the eyebrow, title and lead. Children, aside and footer render as given. */
    loading?: boolean;
}

/**
 * The card at the top of a page: eyebrow, `h1`, lead, then controls, with an optional aside
 * and footer row. One place owns the type scale and the layout, so the heroes stay in step.
 */
export function PageHero({ eyebrow, title, lead, leadWidth = 46, children, aside, footer, loading }: PageHeroProps) {
    return (
        <section className={`${styles.hero} ${aside ? styles.withAside : ""}`}>
            <Box miw={0}>
                {loading ? (
                    <>
                        <Skeleton height={14} mb="sm" width={160} />
                        <Skeleton height={64} mb="md" width="60%" />
                        <Skeleton height={14} mb={8} width="90%" />
                        <Skeleton height={14} width="70%" />
                    </>
                ) : (
                    <>
                        <Text
                            c="light-dark(var(--mantine-color-teal-8), var(--mantine-color-teal-4))"
                            fw={700}
                            fz="0.8125rem"
                            lts="0.1em"
                            mb="xs"
                            tt="uppercase">
                            {eyebrow}
                        </Text>
                        <Title
                            fw={700}
                            fz="clamp(2.25rem, 5vw, 3.5rem)"
                            lh={1}
                            lts="-0.03em"
                            mb="sm"
                            order={1}
                            style={{ overflowWrap: "anywhere" }}>
                            {title}
                        </Title>
                        {lead && (
                            <Text c="dimmed" fz="1.125rem" lh={1.55} maw={`${leadWidth}ch`}>
                                {lead}
                            </Text>
                        )}
                    </>
                )}
                {children && <Box mt="md">{children}</Box>}
            </Box>
            {aside}
            {footer && <div className={styles.footer}>{footer}</div>}
        </section>
    );
}
